import { create } from 'zustand';
import type { BotDefaults } from '@/lib/bots/botDefaults';
import { resetSavedGames } from './savedGames';
import { resetMinimizedGames } from './minimizedGames';
import { resetLiveHotkeys } from './liveHotkeys';

const TOKEN_KEY = 'g1mt_access_token';
const REFRESH_KEY = 'g1mt_refresh_token';
const USER_KEY = 'g1mt_user';
const REFRESH_INTERVAL_MS = 11 * 60 * 60 * 1000; // refresh 1h before 12h expiry

export type UserRole = 'admin' | 'manager' | 'trader';

export interface AuthUser {
  id: string;
  username: string;
  role: UserRole;
  orgId: string | null;
  /** Hard ceiling on Σ open contracts. Null = unlimited. Source of truth is
   *  the relay /api/trader/me endpoint; login/bootstrap responses don't
   *  carry it, so it's populated by refreshMe() shortly after auth. */
  maxContracts?: number | null;
  /** Hard ceiling on count of open (ticker, side) positions. */
  maxPositions?: number | null;
  /** Whether the trader's org is enrolled in Kalshi's Market Maker Program.
   *  Source of truth is /api/trader/me; login/bootstrap don't carry it, so
   *  it's populated by refreshMe() shortly after auth. Undefined → false
   *  (not enrolled). Gates the maker-rebate display. */
  mmRebateEnabled?: boolean;
  /** Resolved bot-flow access = org.botsEnabled && this trader's botsEnabled.
   *  Source of truth /api/trader/me; populated by refreshMe(). Undefined →
   *  false. Gates every bot-flow surface (spawn, claim, bot views). */
  botAccess?: boolean;
  /** Resolved oscar overtake/blocker access (traderRules.oscarEnabled,
   *  fail-closed server-side). Source of truth /api/trader/me; populated by
   *  refreshMe(). Undefined → false. Gates every oscar surface (ticket mode,
   *  release button, settings). */
  oscarAccess?: boolean;
  /** Resolved armed one-click placement access (traderRules.oneClickEnabled,
   *  fail-closed server-side). Undefined is disabled. */
  oneClickAccess?: boolean;
  /** Resolved Live Trading V2 surface access (traderRules.lt2Enabled,
   *  fail-closed server-side, D-020). Undefined is disabled. Gates the
   *  derivative one-click surfaces, two-sided ladders, hotkeys and multi-line
   *  view here; the relay independently re-checks the same grant on every
   *  LT2-tagged order, so this field is presentation and never authority. */
  lt2Access?: boolean;
  /** Server-side per-order ceiling for `lt-` tagged orders
   *  (traderRules.oneClickMaxContracts). Null = relay default (500). */
  oneClickMaxContracts?: number | null;
  /** Resolved Live Trading V3 Attack (W/E) EFFECTIVE access: true only when
   *  the grant AND an explicit attack ceiling are both present, because the
   *  order path deterministically refuses an attack without the ceiling — a
   *  control that armed on the grant alone would advertise a guaranteed
   *  rejection. Undefined is disabled; the relay re-checks everything on
   *  every `__attack` order, so this field is presentation only. */
  attackAccess?: boolean;
  /** Server-side per-order ceiling for `__attack` tagged orders
   *  (traderRules.attackMaxContracts). Null = no explicit ceiling, which the
   *  relay refuses outright — never "unlimited". */
  attackMaxContracts?: number | null;
  /** This trader's personal bot-default overrides (null = none). Resolved
   *  personal → org → edge built-in at spawn. */
  botDefaults?: BotDefaults | null;
  /** The org's baseline bot defaults (null = none). The org layer of the
   *  spawn-config resolution. */
  orgBotDefaults?: BotDefaults | null;
}

export type AuthStatus =
  | 'idle'
  | 'loading'
  | 'authenticated'
  | 'unauthenticated';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  status: AuthStatus;
  needsSetup: boolean;
  error: string | null;

  checkBootstrap: () => Promise<void>;
  bootstrap: (username: string, password: string) => Promise<void>;
  login: (username: string, password: string) => Promise<void>;
  refresh: () => Promise<boolean>;
  logout: () => Promise<void>;
  hydrate: () => void;
  /** Re-fetches /api/trader/me and merges any updated fields (notably the
   *  maxContracts/maxPositions caps) into the persisted AuthUser. Safe to
   *  call any time after auth; no-op if logged out. */
  refreshMe: () => Promise<void>;
}

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let _inflightRefresh: Promise<boolean> | null = null;
let _crossTabBound = false;
let _loggingOut = false;

function startRefreshTimer(refreshFn: () => Promise<boolean>) {
  stopRefreshTimer();
  refreshTimer = setInterval(() => {
    refreshFn().catch(() => {});
  }, REFRESH_INTERVAL_MS);
}

function stopRefreshTimer() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function persist(token: string, refreshToken: string, user: AuthUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(REFRESH_KEY, refreshToken);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function clearPersisted() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

/** Wipe every cross-component per-user cache so a logout (in THIS tab or a
 *  sibling tab via the storage event) can't leak the previous user's
 *  positions/orders/headroom/rate-limit into the next session. Dynamic imports
 *  keep authStore free of UI-layer deps. Must be run on EVERY path to
 *  unauthenticated — both logout() and the cross-tab storage handler. */
function purgeUserCaches() {
  // Synchronous, unlike the dynamic imports below. Those defer because they
  // reach into hook modules and authStore must stay free of UI-layer deps;
  // the mark stores are leaves with no such deps, and a cross-account boundary
  // should not be left open for even a microtask. See resetSavedGames.
  resetSavedGames();
  resetMinimizedGames();
  // #725 — the key mapping decides which keystroke places an order, so the
  // next trader on this browser must never inherit it. Persisted mappings are
  // left intact; signing back in restores your own.
  resetLiveHotkeys();
  void import('../hooks/useHeadroom').then((m) => m.resetHeadroomCache()).catch(() => {});
  void import('../hooks/useUserKalshiPOS').then((m) => m.resetUserKalshiPOSCache()).catch(() => {});
  void import('../hooks/useRateLimitStatus').then((m) => m.resetRateLimitStatusCache()).catch(() => {});
  void import('../hooks/usePolymarketRelayOrders').then((m) => m.resetPolyOrdersShare()).catch(() => {});
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  refreshToken: null,
  user: null,
  status: 'idle',
  needsSetup: false,
  error: null,

  hydrate: () => {
    // Cross-tab/window token sync: adopt token changes written by sibling tabs or
    // popout windows (/fills) so a tab never refreshes with a now-
    // stale in-memory token (the cross-context refresh storm that drove the
    // flicker incident), and so a logout in one context propagates to all. Bound
    // once per page. See project_position_flicker_incident.
    if (!_crossTabBound && typeof window !== 'undefined') {
      _crossTabBound = true;
      window.addEventListener('storage', (e) => {
        if (e.key && e.key !== TOKEN_KEY && e.key !== REFRESH_KEY && e.key !== USER_KEY) return;
        // A deliberate logout in THIS tab is in flight — don't let a sibling tab's
        // racing refresh (which may rewrite tokens) resurrect authed state in a
        // tab the user just logged out of.
        if (_loggingOut) return;
        const t = localStorage.getItem(TOKEN_KEY);
        const r = localStorage.getItem(REFRESH_KEY);
        const u = localStorage.getItem(USER_KEY);
        if (t && r && u) {
          try {
            set({ token: t, refreshToken: r, user: JSON.parse(u) as AuthUser, status: 'authenticated', error: null });
            startRefreshTimer(get().refresh); // proactively refresh however we became authed
          } catch { /* ignore malformed cross-tab payload */ }
        } else {
          stopRefreshTimer();
          // A cross-tab logout cleared the tokens — purge per-user caches here
          // too (this branch does NOT call logout()), else the shared poller
          // keeps the previous user's positions/orders cached + polling.
          purgeUserCaches();
          set({ token: null, refreshToken: null, user: null, status: 'unauthenticated' });
        }
      });
    }
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_KEY);
    const userJson = localStorage.getItem(USER_KEY);
    if (token && refreshToken && userJson) {
      try {
        const user = JSON.parse(userJson) as AuthUser;
        set({ token, refreshToken, user, status: 'authenticated', error: null });
        startRefreshTimer(get().refresh);
        // Eagerly refresh the access token — it's likely expired after page close.
        // This runs concurrently with the initial render; apiFetch 401 retries
        // will coalesce onto the same in-flight promise via _inflightRefresh.
        get().refresh().catch(() => {});
      } catch {
        clearPersisted();
        set({ status: 'unauthenticated' });
      }
    } else {
      set({ status: 'unauthenticated' });
    }
  },

  checkBootstrap: async () => {
    set({ status: 'loading', error: null });
    try {
      const res = await fetch('/api/bootstrap');
      const data = await res.json();
      set({
        needsSetup: data.needsSetup === true,
        status: data.needsSetup ? 'unauthenticated' : get().status,
      });
      if (!data.needsSetup) {
        get().hydrate();
      }
    } catch {
      set({ status: 'unauthenticated', error: 'Failed to check server status' });
    }
  },

  bootstrap: async (username, password) => {
    set({ status: 'loading', error: null });
    try {
      const res = await fetch('/api/bootstrap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        set({ status: 'unauthenticated', error: data.error ?? 'Bootstrap failed' });
        return;
      }
      const user: AuthUser = {
        id: data.user.id,
        username: data.user.username,
        role: data.user.role,
        orgId: data.user.orgId ?? null,
      };
      persist(data.accessToken, data.refreshToken, user);
      set({
        token: data.accessToken,
        refreshToken: data.refreshToken,
        user,
        status: 'authenticated',
        needsSetup: false,
        error: null,
      });
      startRefreshTimer(get().refresh);
    } catch {
      set({ status: 'unauthenticated', error: 'Network error' });
    }
  },

  login: async (username, password) => {
    set({ status: 'loading', error: null });
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        set({ status: 'unauthenticated', error: data.error ?? 'Login failed' });
        return;
      }
      const user: AuthUser = {
        id: data.user.id,
        username: data.user.username,
        role: data.user.role,
        orgId: data.user.orgId ?? null,
      };
      persist(data.accessToken, data.refreshToken, user);
      set({
        token: data.accessToken,
        refreshToken: data.refreshToken,
        user,
        status: 'authenticated',
        error: null,
      });
      startRefreshTimer(get().refresh);
    } catch {
      set({ status: 'unauthenticated', error: 'Network error' });
    }
  },

  refresh: async () => {
    if (_inflightRefresh) return _inflightRefresh;

    const execute = async (): Promise<boolean> => {
      const rt = get().refreshToken;
      if (!rt) return false;
      try {
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: rt }),
        });
        if (!res.ok) {
          // Only a definitive auth rejection tears down the session. A transient
          // failure (network / 5xx relay blip) must NOT log the user out — that
          // unmounts the whole dashboard (the flicker class). Keep the session;
          // the refresh timer / next apiFetch retries.
          if (res.status === 401 || res.status === 403) {
            await get().logout();
          }
          return false;
        }
        const data = await res.json().catch(() => null) as {
          accessToken?: unknown;
          refreshToken?: unknown;
        } | null;
        if (
          !data
          || typeof data.accessToken !== 'string'
          || typeof data.refreshToken !== 'string'
        ) {
          await get().logout();
          return false;
        }
        const user = get().user;
        if (user) {
          persist(data.accessToken, data.refreshToken, user);
        }
        set({ token: data.accessToken, refreshToken: data.refreshToken });
        return true;
      } catch {
        return false;
      }
    };

    _inflightRefresh = execute().finally(() => { _inflightRefresh = null; });
    return _inflightRefresh;
  },

  refreshMe: async () => {
    const { token, user } = get();
    if (!token || !user) return;
    try {
      const res = await apiFetch('/api/trader/me');
      if (!res.ok) return;
      const data = (await res.json()) as Partial<AuthUser>;
      const next: AuthUser = {
        ...user,
        ...(typeof data.username === 'string' ? { username: data.username } : {}),
        ...(data.role ? { role: data.role } : {}),
        orgId: data.orgId ?? user.orgId,
        maxContracts: data.maxContracts ?? null,
        maxPositions: data.maxPositions ?? null,
        mmRebateEnabled: data.mmRebateEnabled ?? false,
        botAccess: data.botAccess ?? false,
        oscarAccess: data.oscarAccess ?? false,
        oneClickAccess: data.oneClickAccess ?? false,
        lt2Access: data.lt2Access ?? false,
        oneClickMaxContracts: data.oneClickMaxContracts ?? null,
        attackAccess: data.attackAccess ?? false,
        attackMaxContracts: data.attackMaxContracts ?? null,
        botDefaults: data.botDefaults ?? null,
        orgBotDefaults: data.orgBotDefaults ?? null,
      };
      const rt = get().refreshToken;
      const tok = get().token;
      if (tok && rt) persist(tok, rt, next);
      set({ user: next });
    } catch {
      // Best-effort — limits widget falls back to "unknown" if this fails.
    }
  },

  logout: async () => {
    const rt = get().refreshToken;
    // Suppress cross-tab re-adopt briefly so a sibling tab's in-flight refresh
    // can't resurrect this just-logged-out session via the storage event.
    _loggingOut = true;
    setTimeout(() => { _loggingOut = false; }, 5000);
    stopRefreshTimer();
    clearPersisted();
    purgeUserCaches();
    set({
      token: null,
      refreshToken: null,
      user: null,
      status: 'unauthenticated',
      error: null,
    });
    if (rt) {
      fetch('/api/auth/logout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      }).catch(() => {});
    }
  },
}));

/**
 * Authenticated fetch wrapper. Attaches JWT, retries once on 401 via refresh.
 */
export async function apiFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const { token } = useAuthStore.getState();
  const headers = new Headers(init.headers);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let res = await fetch(path, { ...init, headers });

  if (res.status === 401) {
    const refreshed = await useAuthStore.getState().refresh();
    if (refreshed) {
      const newToken = useAuthStore.getState().token;
      if (newToken) headers.set('Authorization', `Bearer ${newToken}`);
      res = await fetch(path, { ...init, headers });
    }
  }

  return res;
}
